// Where a thing belongs: the workspace icon at a detail's top, and the menu
// that moves it. A Case, a chat or a document belongs to one workspace (or
// none); a person to any number.
//
// Promise:
// - The button shows the workspace's icon, an empty ring when there is none.
// - Choosing a workspace the person is in moves it there; the row for no
//   workspace takes it out. A workspace the person left shows as the current
//   value (its last name) and is not a place to move to.
// - With no cloud to reach (a Vault alone) the button is drawn but cannot be
//   pressed.
import { m } from "@trameria/i18n";
import { useMemo } from "react";
import { apiErrorWithLead } from "../data/errors";
import { HEADER_ICON_BUTTON } from "../shell/header-data";
import type { ShellActionInput } from "../shell/outside/outside";
import { MenuButton, type MenuItem } from "../ui/menu";
import { toast } from "../ui/toast";
import { WorkspaceTile } from "./mark";
import {
  useMoveWorkspace,
  useWorkspaces,
  type WorkspaceMark,
  type WorkspaceThing,
} from "./queries";

interface Entry {
  mark: WorkspaceMark;
  on: boolean;
  /** Pressing it does something (a workspace left is only shown). */
  pickable: boolean;
}

/** The header's button for where `thing` belongs (`ids`: the ones it has now). */
export function useWorkspaceAction(
  thing: WorkspaceThing,
  ids: readonly string[],
  multiple = false,
): ShellActionInput {
  const ws = useWorkspaces();
  const move = useMoveWorkspace(thing);
  const failed = (err: unknown) =>
    toast(apiErrorWithLead(err, m.shell_workspace_failed()));
  const current = ids.flatMap((id) => ws.byId.get(id) ?? []);
  const entries: Entry[] = [
    // What it has now and left: shown first, as it is.
    ...current
      .filter((w) => !w.member)
      .map((mark) => ({ mark, on: true, pickable: false })),
    ...ws.members.map((mark) => ({
      mark,
      on: ids.includes(mark.id),
      pickable: true,
    })),
  ];
  const pick = (e: Entry) => {
    if (!e.pickable || !ws.connected) return;
    if (multiple)
      move.mutate(e.on ? { remove: [e.mark.id] } : { add: [e.mark.id] }, {
        onError: failed,
      });
    else if (!e.on) move.mutate({ workspace: e.mark.id }, { onError: failed });
  };
  // Taking it out (one workspace only): there must be something to take out.
  const takeOut = !multiple && ids.length > 0;
  const label =
    current.length > 0
      ? multiple
        ? m.shell_workspace_change_many({
            names: current.map((w) => w.name).join(", "),
          })
        : m.shell_workspace_change({ name: current[0].name })
      : m.shell_workspace_set();
  return {
    id: "workspace",
    label,
    icon: "workspace",
    disabled: !ws.connected,
    items: [
      ...entries.map((e) => ({
        id: `workspace:${e.mark.id}`,
        label: e.pickable
          ? e.mark.name
          : m.shell_workspace_left({ name: e.mark.name }),
        on: e.on,
        disabled: !e.pickable,
        onPress: () => pick(e),
      })),
      ...(takeOut
        ? [
            ...(entries.length > 0 ? [{ type: "separator" as const }] : []),
            {
              id: "workspace:none",
              label: m.shell_workspace_remove(),
              onPress: () =>
                ws.connected &&
                move.mutate({ workspace: null }, { onError: failed }),
            },
          ]
        : []),
    ],
    render: () => (
      <WorkspaceButton
        label={label}
        entries={entries}
        current={current}
        takeOut={takeOut}
        pick={pick}
        onTakeOut={() =>
          ws.connected && move.mutate({ workspace: null }, { onError: failed })
        }
      />
    ),
  };
}

function WorkspaceButton({
  label,
  entries,
  current,
  takeOut,
  pick,
  onTakeOut,
}: {
  label: string;
  entries: readonly Entry[];
  current: readonly WorkspaceMark[];
  takeOut: boolean;
  pick(e: Entry): void;
  onTakeOut(): void;
}) {
  const ws = useWorkspaces();
  const items = useMemo<(MenuItem | "separator")[]>(
    () => [
      ...entries.map(
        (e): MenuItem => ({
          id: `workspace:${e.mark.id}`,
          label: e.pickable
            ? e.mark.name
            : m.shell_workspace_left({ name: e.mark.name }),
          icon: e.on ? "check" : undefined,
          disabled: !e.pickable,
          onSelect: () => pick(e),
        }),
      ),
      ...(takeOut
        ? [
            ...(entries.length > 0 ? ["separator" as const] : []),
            {
              id: "workspace:none",
              label: m.shell_workspace_remove(),
              onSelect: onTakeOut,
            },
          ]
        : []),
    ],
    [entries, takeOut, pick, onTakeOut],
  );
  const glyph =
    current.length === 0 ? (
      <span
        aria-hidden="true"
        className="h-[18px] w-[18px] rounded-full border border-dashed border-faint"
      />
    ) : (
      <span className="inline-flex items-center">
        {current.slice(0, 3).map((w, i) => (
          <span
            key={w.id}
            className={`inline-flex rounded-full ring-2 ring-surface ${i > 0 ? "-ml-2" : ""}`}
          >
            <WorkspaceTile workspace={w} ws={ws} size={18} />
          </span>
        ))}
      </span>
    );
  const style = `${HEADER_ICON_BUTTON} ${current.length > 1 ? "w-auto min-w-[26px] px-1" : ""}`;
  if (!ws.connected)
    return (
      <button
        type="button"
        disabled
        aria-label={label}
        title={m.shell_workspace_offline()}
        className={`${style} cursor-default opacity-60 hover:bg-transparent hover:text-muted`}
      >
        {glyph}
      </button>
    );
  return (
    <MenuButton label={label} align="end" className={style} items={items}>
      {glyph}
    </MenuButton>
  );
}

/** The detail header's button, for a screen that draws its own. */
export function WorkspaceHeaderButton({
  thing,
  ids,
  multiple = false,
}: {
  thing: WorkspaceThing;
  ids: readonly string[];
  multiple?: boolean;
}) {
  const action = useWorkspaceAction(thing, ids, multiple);
  return <>{action.render?.(false)}</>;
}
